import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";
import { auth, isLoggingOut } from "./auth";
import * as Sentry from "@sentry/vue";
import posthog from "posthog-js";
import posthogConfig from "./posthog.config";
import { maskNetworkRequest } from "./analytics";
import { followOtherTabs } from "./workspace";

const app = createApp(App);
app.use(store);
app.use(vuetify);
followOtherTabs();

// enabled only for production builds: a local/test run must never post into
// the live Sentry project, same rule as posthog.config.js and for the same
// reason (dev noise would drown out real prod issues in triage)
const sentryEnabled = import.meta.env.PROD;

// single decision governing both sides of the replay handoff: whenever
// PostHog isn't going to init (dev, test), Sentry must keep its own session
// sampling, or no non-error replay is recorded anywhere
const posthogEnabled = posthogConfig.enabled;

try {
  if (sentryEnabled) {
    Sentry.init({
      app,
      dsn: "https://0ceced49ff47a019b40f25dfb8fdb64a@o4508388225974272.ingest.de.sentry.io/4508388227350608",
      // matches the release the build injects into the uploaded sourcemaps
      // (see package.json's "build"/"sentry:sourcemaps" scripts); without this
      // events had no release tag, making it impossible to tell which deploy an
      // error came from
      release: import.meta.env.VITE_SENTRY_RELEASE,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
          networkDetailAllowUrls: [
            window.location.origin,
            "api.1link.io",
            /^https:\/\/api\.1link\.io/,
          ],
          // never record a body that holds a secret: GET /user carries
          // supportIdentityHash (whoever holds it reads the person's PostHog
          // Support tickets), /user/api-key lists and creates full api keys
          networkDetailDenyUrls: [
            /^https:\/\/api\.1link\.io\/v1\/user\/?(\?|$)/,
            /^https:\/\/api\.1link\.io\/v1\/user\/api-key/,
          ],
          // link/domain/alias/deep-linking form fields aren't sensitive,
          // unmask them so replays are actually useful for debugging those flows
          unmask: [".unmask-form"],
        }),
      ],
      tracesSampleRate: 1.0,
      // PostHog owns behavioural session replay when it's actually enabled;
      // otherwise fall back to Sentry's own sampling so replay isn't lost
      replaysSessionSampleRate: posthogEnabled ? 0 : 0.1,
      replaysOnErrorSampleRate: 1.0,
    });
  }
} catch (err) {
  console.log("sentry err", err);
}

try {
  if (posthogEnabled) {
    posthog.init(posthogConfig.key, {
      api_host: posthogConfig.host,
      person_profiles: "identified_only",
      cross_subdomain_cookie: true, // stitch 1link.io <-> app.1link.io
      session_recording: {
        minimum_duration_milliseconds: 2000, // drop bounce sessions
        // link/domain/alias/deep-linking form fields aren't sensitive,
        // unmask them so replays are actually useful for debugging those flows
        maskInputFn: (text, element) =>
          element?.closest(".unmask-form") ? text : "*".repeat(text.length),
        maskCapturedNetworkRequestFn: maskNetworkRequest,
      },
    });
  }
} catch (err) {
  console.log("posthog err", err);
}

let mounted = false;
// allow to load the user (and set it to vuex) before rendering vuejs
auth().onAuthStateChanged((user) => {
  // already rendered vuejs, return
  if (mounted) {
    // the session ended without logout(): firebase drops every session of an
    // account once an email change is confirmed or its password is reset, and
    // a sign-out in another tab arrives here too. The app has no user from
    // then on and each api call fails, so reload: the guard sends to /login
    if (!user && !isLoggingOut()) {
      window.location.reload();
    }
    return;
  }
  mounted = true;

  if (user) {
    // is auth
    store.commit("setUser", user);
  }

  // vue-router 4+ starts its initial navigation on install, and the global
  // guard reads auth().currentUser: installing before firebase has restored
  // the session would bounce a reload of /#/links to /login
  app.use(router);
  // mount only once the initial navigation settled: on the first render the
  // route is START_LOCATION (meta {}), so App.vue's `!$route.meta.removeBanner`
  // would paint the drawer/footer for a frame on /login, /signup, /terms.
  // isReady() resolves after the first navigation settles (also when it was
  // redirected); if it rejects, still mount rather than leave a blank page.
  router.isReady().finally(() => app.mount("#app"));
});
